import { useState } from 'react'
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined'
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined'
import { PERSONA_STATUS_KEYS } from '../../helpers/personas/personaStatusLabels'
import useStatusDisplays from '../../hooks/useStatusDisplay'

// Persona lifecycle statuses, in ladder order. Personas without a Status
// field count as 'active'. 'all' disables the status narrowing.
//
// Alleen de LABELS volgen de instelling van de organisatie, niet de
// zichtbaarheid: dit is een analysefilter zonder tegenhanger, dus een status
// hier weglaten zou de deelnemers die hem dragen onselecteerbaar maken.
// De korte schrijfwijze van 'outflow' verviel daarmee — het eigen woord van de
// organisatie gaat voor, en zonder instelling is dat 'Uitgestroomd'.
const statusOptions = (labels) => [
    ...PERSONA_STATUS_KEYS.map((key) => ({ id: key, label: labels[key] })),
    { id: 'all', label: 'Alle statussen' },
]

const formatCount = (count) => {
    if (count === undefined || count === null) return ''
    const label = count === 1 ? 'antwoord' : 'antwoorden'
    return ` (${count} ${label})`
}

/** @param {string | undefined} detail */
const formatDetail = (detail) => (detail ? ` · ${detail}` : '')

/** @param {{ Status?: string }} persona */
const personaStatus = (persona) => persona?.Status || 'active'

/**
 * Persona-filter chips: a status chip that narrows the persona list, and a
 * persona chip with the personas of that status. The persona selection is
 * controlled — caller owns the selected ID and decides what `onChange` does
 * (local React state in analytics, Firestore write in dashboard). The status
 * choice is internal UI state: it only conditions which personas are listed.
 *
 * @param {object} props
 * @param {Array<{ID: string, Name: string, Status?: string}>} props.personas - Selectable personas (all statuses). Renders nothing when empty.
 * @param {string} [props.selectedPersonaId] - Currently selected persona ID, or '' / falsy for "all".
 * @param {(newId: string) => void} props.onChange - Called with the new persona ID (or '' for "all").
 * @param {{ [personaId: string]: number }} [props.responseCounts] - Optional response counts shown after each persona name.
 * @param {string} [props.label='Medewerkers'] - Chip label before the persona select
 * @param {string} [props.allLabel='Alle'] - Label of the "no persona" option
 * @param {(persona: {ID: string, Name: string, Status?: string}) => string} [props.optionDetail] - Optional extra text after each persona name (e.g. status and start date); empty string = nothing
 */
const DashboardPersonaFilter = ({
    personas,
    selectedPersonaId,
    onChange,
    responseCounts,
    label = 'Medewerkers',
    allLabel = 'Alle',
    optionDetail,
}) => {
    // null until the user picks a status — then the filter follows the
    // selected persona's status (so a saved selection stays visible),
    // falling back to 'active'.
    const [statusChoice, setStatusChoice] = useState(/** @type {string | null} */ (null))
    // Boven de vroege return: het aantal hooks mag per render niet wisselen.
    const labels = useStatusDisplays().persona.labels

    if (!Array.isArray(personas) || personas.length === 0) return null

    const selectedPersona = personas.find((p) => p.ID === selectedPersonaId)
    const statusFilter = statusChoice || (selectedPersona ? personaStatus(selectedPersona) : 'active')

    const visiblePersonas = statusFilter === 'all'
        ? personas
        : personas.filter((p) => personaStatus(p) === statusFilter)

    const handleStatusChange = (e) => {
        const newStatus = e.target.value
        setStatusChoice(newStatus)
        // The selected persona falls outside the new status → back to "Alle"
        if (selectedPersona && newStatus !== 'all' && personaStatus(selectedPersona) !== newStatus) {
            onChange('')
        }
    }

    const handlePersonaChange = (e) => {
        onChange(e.target.value)
    }

    return (
        <>
            <label className="dashboard-filter-chip">
                <PersonOutlineOutlinedIcon fontSize="small" className="dashboard-filter-chip-icon" />
                <span className="dashboard-filter-chip-label">{label}:</span>
                <select
                    className="dashboard-filter-chip-select"
                    value={selectedPersonaId || ''}
                    onChange={handlePersonaChange}
                >
                    <option value="">{allLabel}</option>
                    {visiblePersonas.map((p) => (
                        <option key={p.ID} value={p.ID}>
                            {p.Name}{formatDetail(optionDetail?.(p))}{formatCount(responseCounts?.[p.ID])}
                        </option>
                    ))}
                </select>
            </label>
            <label className="dashboard-filter-chip">
                <FlagOutlinedIcon fontSize="small" className="dashboard-filter-chip-icon" />
                <span className="dashboard-filter-chip-label">Status:</span>
                <select
                    className="dashboard-filter-chip-select"
                    value={statusFilter}
                    onChange={handleStatusChange}
                >
                    {statusOptions(labels).map((opt) => (
                        <option key={opt.id} value={opt.id}>
                            {opt.label}
                        </option>
                    ))}
                </select>
            </label>
        </>
    )
}

export default DashboardPersonaFilter
